import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase";
import { isOpenFeedDrop } from "./dropsFeedService";
import { normalizeCreator } from "../utils/homeSeedPreview";
import { waitForAuthReady } from "../utils/waitForAuthReady";

const HISTORY_LIMIT = 20;

/** Maps platform/featured seed UIDs → usernames when user docs use auth UIDs instead. */
const SEED_CREATOR_USERNAMES = {
  demo_user_001: "sophiakate",
  demo_user_002: "alexmarie",
  demo_user_003: "marcojr",
  demo_user_004: "priyafitness",
  demo_user_005: "jakebuilds",
  demo_user_006: "chloepaints",
  demo_user_007: "tylerchef",
  demo_user_008: "niajourney",
  demo_user_009: "liamlens",
  demo_user_010: "devonskits",
  demo_user_011: "kaiplays",
  demo_user_012: "mayaglow",
  demo_user_013: "colerink",
  demo_user_014: "reedcapital",
  demo_user_015: "lunaflow",
  demo_user_016: "inkbydani",
  demo_user_017: "solebysam",
  demo_user_018: "peaktheo",
  demo_user_019: "audiojune",
  demo_user_020: "waxandwick",
};

/** Card-ready models from denormalized platform/featured preview snapshots. */
export function normalizePreviewCreators(previews = []) {
  return previews
    .filter(Boolean)
    .map((entry, index) => ({
      id: entry.creatorId || entry.id || `preview_${index}`,
      creatorId: entry.creatorId || entry.id,
      displayName: entry.displayName || entry.handle || entry.username || "Creator",
      handle: entry.handle || entry.username || "",
      username: entry.username || entry.handle || "",
      avatar: entry.avatar || entry.avatarUrl || "",
      avatarUrl: entry.avatarUrl || entry.avatar || "",
      rank: index + 1,
      nominationCount: entry.nominationCount || 0,
      openDropCount: entry.openDropCount || 0,
    }));
}

async function fetchUserForFeaturedCreatorId(creatorId) {
  const directSnap = await getDoc(doc(db, "users", creatorId));
  if (directSnap.exists()) {
    return {
      id: directSnap.id,
      uid: directSnap.id,
      featuredId: creatorId,
      ...directSnap.data(),
    };
  }

  const username = SEED_CREATOR_USERNAMES[creatorId];
  if (username) {
    const byUsername = await getDocs(
      query(
        collection(db, "users"),
        where("username", "==", username),
        limit(1)
      )
    );
    if (!byUsername.empty) {
      const match = byUsername.docs[0];
      console.log(
        `[Featured] Resolved ${creatorId} via username @${username} → users/${match.id}`
      );
      return {
        id: match.id,
        uid: match.id,
        featuredId: creatorId,
        ...match.data(),
      };
    }
  }

  console.warn(`[Featured] No users/${creatorId} document (username fallback failed)`);
  return null;
}

async function fetchFeaturedUsersByIds(creatorIds) {
  const ids = [...new Set(creatorIds.filter(Boolean))];
  if (!ids.length) return [];

  const results = await Promise.all(ids.map(fetchUserForFeaturedCreatorId));
  return results.filter(Boolean);
}

async function fetchNominationCountsForIds(creatorIds) {
  const map = new Map();
  await Promise.all(
    creatorIds.map(async (creatorId) => {
      try {
        const snap = await getDoc(doc(db, "nominations", creatorId));
        if (!snap.exists()) return;
        const data = snap.data();
        const count =
          typeof data.count === "number"
            ? data.count
            : typeof data.nominationCount === "number"
              ? data.nominationCount
              : 0;
        map.set(creatorId, count);
      } catch (err) {
        console.warn(`[Featured] nominations/${creatorId} read failed:`, err.message);
      }
    })
  );
  return map;
}

async function fetchNominationCountMap() {
  try {
    const snap = await getDocs(collection(db, "nominations"));
    const map = new Map();

    snap.docs.forEach((d) => {
      const data = d.data();
      const creatorId = data.creatorId || d.id;
      const add =
        typeof data.count === "number"
          ? data.count
          : typeof data.nominationCount === "number"
            ? data.nominationCount
            : 1;
      map.set(creatorId, (map.get(creatorId) || 0) + add);
    });

    return map;
  } catch (err) {
    console.warn("[Featured] nominations collection read failed:", err.message);
    return new Map();
  }
}

export async function fetchPlatformFeatured() {
  await waitForAuthReady();

  const snap = await getDoc(doc(db, "platform", "featured"));

  if (!snap.exists()) {
    console.log("[Featured] platform/featured: document does not exist");
    return {
      discoveryCreators: [],
      communityCreators: [],
      discoveryPreview: [],
      communityPreview: [],
      totalNominationsCast: 0,
      featuredDate: null,
    };
  }

  const data = snap.data();
  const payload = {
    discoveryCreators: data.discoveryCreators || [],
    communityCreators: data.communityCreators || [],
    discoveryPreview: normalizePreviewCreators(data.discoveryPreview || []),
    communityPreview: normalizePreviewCreators(data.communityPreview || []),
    totalNominationsCast: data.totalNominationsCast || 0,
    featuredDate: data.featuredDate || data.date || null,
    updatedAt: data.updatedAt || null,
  };

  console.log("[Featured] platform/featured:", {
    discoveryCreators: payload.discoveryCreators,
    communityCreators: payload.communityCreators,
    discoveryPreviewCount: payload.discoveryPreview.length,
    communityPreviewCount: payload.communityPreview.length,
    totalNominationsCast: payload.totalNominationsCast,
  });

  return payload;
}

export async function fetchFeaturedHistory(max = HISTORY_LIMIT) {
  try {
    const snap = await getDocs(
      query(
        collection(db, "creatorFeaturedHistory"),
        orderBy("lastFeatured", "desc"),
        limit(max)
      )
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn("creatorFeaturedHistory orderBy failed, using client sort:", err.message);
    const snap = await getDocs(
      query(collection(db, "creatorFeaturedHistory"), limit(max))
    );
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort(
        (a, b) =>
          (b.lastFeatured?.seconds || 0) - (a.lastFeatured?.seconds || 0)
      )
      .slice(0, max);
  }
}

export async function fetchNominationLeaderboard(max = 8) {
  const countMap = await fetchNominationCountMap();
  const sorted = [...countMap.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, max);

  if (!sorted.length) return [];

  const users = await fetchFeaturedUsersByIds(sorted.map(([id]) => id));
  const userByFeaturedId = new Map(
    users.map((u) => [u.featuredId || u.id, normalizeCreator(u.id, u)])
  );

  return sorted.map(([creatorId, count], index) => ({
    rank: index + 1,
    creatorId,
    count,
    creator: userByFeaturedId.get(creatorId) || null,
  }));
}

export async function fetchTotalNominationsCast(fallbackFromPlatform = 0) {
  const map = await fetchNominationCountMap();
  const total = [...map.values()].reduce((sum, n) => sum + n, 0);
  return total || fallbackFromPlatform;
}

async function fetchOpenDropsForCreatorIds(creatorIdToDocId) {
  const byFeaturedId = {};

  await Promise.all(
    [...creatorIdToDocId.entries()].map(async ([featuredId, docId]) => {
      byFeaturedId[featuredId] = [];
      const idsToTry = [...new Set([featuredId, docId].filter(Boolean))];

      for (const dropCreatorId of idsToTry) {
        try {
          const snap = await getDocs(
            query(
              collection(db, "drops"),
              where("creatorId", "==", dropCreatorId),
              limit(12)
            )
          );
          const open = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter(isOpenFeedDrop);
          if (open.length) {
            byFeaturedId[featuredId] = open;
            break;
          }
        } catch (err) {
          console.warn(`[Featured] drops query for ${dropCreatorId} failed:`, err.message);
        }
      }
    })
  );

  return byFeaturedId;
}

function viewsLeft(drop) {
  return Math.max((drop.maxViews || 100) - (drop.viewCount || 0), 0);
}

function mergeCreatorsWithPreviews(resolved, previews) {
  if (resolved?.length) return resolved;
  return previews || [];
}

/**
 * Resolve creator IDs from platform/featured into enriched card models.
 * Falls back to denormalized preview snapshots when user reads fail.
 */
export async function resolveFeaturedCreators(
  creatorIds,
  { includeNominationCounts = false, previewFallback = [] } = {}
) {
  if (!creatorIds?.length) {
    if (previewFallback.length) {
      console.log("[Featured] resolveFeaturedCreators: using preview fallback only");
      return previewFallback;
    }
    console.log("[Featured] resolveFeaturedCreators: no creator IDs provided");
    return [];
  }

  await waitForAuthReady();

  console.log("[Featured] resolveFeaturedCreators: fetching", creatorIds);

  let users = [];
  let nominationMap = new Map();
  let openDropsByCreator = {};

  try {
    users = await fetchFeaturedUsersByIds(creatorIds);
  } catch (err) {
    console.error("[Featured] fetchFeaturedUsersByIds failed:", err);
  }

  const creatorIdToDocId = new Map(
    creatorIds.map((id) => {
      const user = users.find((u) => (u.featuredId || u.id) === id || u.id === id);
      return [id, user?.id || id];
    })
  );

  try {
    [nominationMap, openDropsByCreator] = await Promise.all([
      includeNominationCounts
        ? fetchNominationCountsForIds(creatorIds)
        : Promise.resolve(new Map()),
      fetchOpenDropsForCreatorIds(creatorIdToDocId),
    ]);
  } catch (err) {
    console.warn("[Featured] nomination/drops enrichment failed:", err.message);
  }

  console.log("[Featured] resolveFeaturedCreators: loaded users", {
    requested: creatorIds.length,
    found: users.length,
    userIds: users.map((u) => u.id),
  });

  const userByFeaturedId = new Map();

  users.forEach((u) => {
    const normalized = normalizeCreator(u.id, u);
    userByFeaturedId.set(u.featuredId || u.id, normalized);
    if (u.featuredId && u.featuredId !== u.id) {
      userByFeaturedId.set(u.featuredId, normalized);
    }
  });

  creatorIds.forEach((featuredId) => {
    if (userByFeaturedId.has(featuredId)) return;
    const username = SEED_CREATOR_USERNAMES[featuredId];
    if (!username) return;
    const match = users.find(
      (u) => u.username === username || u.handle === username
    );
    if (match) {
      userByFeaturedId.set(featuredId, normalizeCreator(match.id, match));
    }
  });

  const resolved = creatorIds
    .map((id, index) => {
      const raw = userByFeaturedId.get(id);
      if (!raw) return null;

      const openDrops = openDropsByCreator[id] || [];
      const urgentDrop = openDrops.sort((a, b) => viewsLeft(a) - viewsLeft(b))[0];

      return {
        ...raw,
        id: raw.id || id,
        rank: index + 1,
        openDropCount: openDrops.length,
        viewsLeft: urgentDrop ? viewsLeft(urgentDrop) : 0,
        nominationCount: nominationMap.get(id) || 0,
        cardImage:
          raw.bannerImage ||
          raw.bannerUrl ||
          urgentDrop?.thumbnailUrl ||
          raw.avatarUrl ||
          raw.avatar ||
          "",
        thumbnailUrl: urgentDrop?.thumbnailUrl || raw.bannerImage || raw.avatarUrl || "",
      };
    })
    .filter(Boolean);

  return mergeCreatorsWithPreviews(resolved, previewFallback);
}

/** Load everything the featured landing page needs, with graceful fallbacks. */
export async function loadFeaturedLandingData() {
  const platform = await fetchPlatformFeatured();

  let discovery = platform.discoveryPreview;
  let community = platform.communityPreview;
  let history = [];
  let nominationTotal = platform.totalNominationsCast || 0;

  try {
    const [resolvedDiscovery, resolvedCommunity, past, totalNoms] =
      await Promise.allSettled([
        resolveFeaturedCreators(platform.discoveryCreators, {
          previewFallback: platform.discoveryPreview,
        }),
        resolveFeaturedCreators(platform.communityCreators, {
          includeNominationCounts: true,
          previewFallback: platform.communityPreview,
        }),
        fetchFeaturedHistory(20),
        fetchTotalNominationsCast(platform.totalNominationsCast),
      ]);

    if (resolvedDiscovery.status === "fulfilled" && resolvedDiscovery.value.length) {
      discovery = resolvedDiscovery.value;
    }
    if (resolvedCommunity.status === "fulfilled" && resolvedCommunity.value.length) {
      community = resolvedCommunity.value;
    }
    if (past.status === "fulfilled") {
      history = past.value;
    } else {
      console.warn("[Featured landing] history load failed:", past.reason);
    }
    if (totalNoms.status === "fulfilled") {
      nominationTotal = totalNoms.value;
    }
  } catch (err) {
    console.error("[Featured landing] enrichment failed, using platform previews:", err);
  }

  console.log("[Featured landing] loaded:", {
    discovery: discovery.map((c) => c.displayName || c.handle),
    community: community.map((c) => c.displayName || c.handle),
    historyCount: history.length,
    nominationTotal,
  });

  return { discovery, community, history, nominationTotal, platform };
}

/**
 * Today's 10 featured creators for the Gallery hero (5 discovery + 5 community).
 * Reads platform/featured and resolves creator profiles with preview fallbacks.
 */
export async function loadGalleryFeaturedCreators() {
  const platform = await fetchPlatformFeatured();

  const [discovery, community] = await Promise.all([
    resolveFeaturedCreators(platform.discoveryCreators, {
      previewFallback: platform.discoveryPreview,
    }),
    resolveFeaturedCreators(platform.communityCreators, {
      includeNominationCounts: true,
      previewFallback: platform.communityPreview,
    }),
  ]);

  const taggedDiscovery = discovery.map((c) => ({ ...c, featuredCategory: "discovery" }));
  const taggedCommunity = community.map((c) => ({ ...c, featuredCategory: "community" }));

  return [...taggedDiscovery, ...taggedCommunity];
}
